import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AppDispatch, RootState } from "../../../../redux/store";
import { fetchCableProviders } from "../../../../features/auth/store/cableProviderSlice";
import CableProvidersTable from "./CableProvidersTable";
import { CreateCableProviderModal } from "../../Modal/Cable/CreateCableProviderModal";
import {
  ExternalContainer,
  InternalContainer,
} from "../../../../components/Reusable/Layout";
import { Button } from "../../../../components/Reusable/Button";
import { EditCableProviderModal } from "../../Modal/Cable/EditCableProviderModal";
import { DeleteCableProviderModal } from "../../Modal/Cable/DeleteCableProviderModal";
import ProviderFaqManagementModal from "../../Modal/ProviderFaqManagementModal";

const CableProviders: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedProvider, setSelectedProvider] = useState<number | null>(null);
  const [selectedFaqProvider, setSelectedFaqProvider] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const { cableProviders, loading } = useSelector(
    (state: RootState) => state.cableProviders,
  );

  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      dispatch(fetchCableProviders({}));
      return;
    }

    const timer = setTimeout(() => {
      dispatch(
        fetchCableProviders({
          search: searchTerm.trim() || undefined,
          status: statusFilter || undefined,
        }),
      );
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, dispatch]);

  const openDeleteModal = async (id: number) => {
    setSelectedProvider(id);
    setIsDeleteModalOpen(true);
  };

  const openEditModal = async (id: number) => {
    setSelectedProvider(id);
    setIsEditModalOpen(true);
  };
  const openFaqModal = (provider: { id: number; name: string }) => {
    setSelectedFaqProvider(provider);
    setIsFaqModalOpen(true);
  };

  return (
    <ExternalContainer>
      <div className="h-full min-h-screen dark:bg-[#FFFFFF1A] w-full max-w-6xl mx-auto px-4 py-6">
        <InternalContainer headerText="Cable Providers">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
            <div className="flex flex-col sm:flex-row gap-4 w-full">
              <input
                type="text"
                placeholder="Search by name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border p-2 rounded w-full sm:w-64"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border p-2 rounded w-full sm:w-40"
              >
                <option value="">All Status</option>
                <option value="on">Active</option>
                <option value="off">Inactive</option>
              </select>
            </div>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
              textColor="text-white"
              borderRadius="rounded-lg"
              className="w-full md:w-[200px] h-10"
            >
              New Provider
            </Button>
          </div>

          <CableProvidersTable
            providers={cableProviders}
            loading={loading}
            searchTerm={searchTerm}
            statusFilter={statusFilter}
            onDelete={openDeleteModal}
            onEdit={openEditModal}
            onManageFaq={openFaqModal}
          />
          <CreateCableProviderModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
          />
          <EditCableProviderModal
            isOpen={isEditModalOpen}
            id={selectedProvider}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedProvider(null);
            }}
          />
          <DeleteCableProviderModal
            id={selectedProvider}
            isOpen={isDeleteModalOpen}
            onClose={() => {
              setIsDeleteModalOpen(false);
              setSelectedProvider(null);
            }}
          />
          <ProviderFaqManagementModal
            isOpen={isFaqModalOpen}
            onClose={() => {
              setIsFaqModalOpen(false);
              setSelectedFaqProvider(null);
            }}
            providerId={selectedFaqProvider?.id ?? null}
            providerName={selectedFaqProvider?.name}
            providerEndpoint="cable-providers"
          />
        </InternalContainer>
        {/* You can add an EditDataProviderModal here, similar to Create */}
      </div>
    </ExternalContainer>
  );
};

export default CableProviders;
